import type { PartTechnology } from "@/lib/harvestTechnologyGroups";

/** FIX-273: stable harvest part ids and "deselected" persistence. */

export const normalisePart = (s: string | null | undefined) =>
  (s ?? "").trim().toLowerCase().replace(/\s+/g, " ");

/** Stable id for an engine part: name + category + technology, no position. */
export function stablePartId(
  name: string | null | undefined,
  category: string | null | undefined,
  technology: string | null | undefined,
): string {
  const tech = normalisePart(technology) || "all";
  return `rec:${normalisePart(name)}|${normalisePart(category)}|${tech}`;
}

export const isEnginePartId = (id: string) => id.startsWith("rec:") || id.startsWith("rec-");

/** Keeps the first item per id; later duplicates are dropped with a warning. */
export function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of items) {
    if (seen.has(item.id)) {
      console.warn(`[harvest] duplicate part id dropped: ${item.id}`);
      continue;
    }
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

/** Effective selection = current parts − deselected − technology-uncertain units. */
export function effectiveSelection(
  allIds: string[],
  deselected: Iterable<string>,
  uncertainIds: ReadonlySet<string>,
): string[] {
  const d = new Set(deselected);
  return allIds.filter((id) => !d.has(id) && !uncertainIds.has(id));
}

/**
 * Converts a wanted selection into the deselected list. Deselected ids of parts
 * that are not in the current list are kept (they may come back); uncertain
 * units are never stored as deselected.
 */
export function deselectedFromSelection(
  allIds: string[],
  nextSelected: string[],
  previousDeselected: string[],
  uncertainIds: ReadonlySet<string>,
): string[] {
  const current = new Set(allIds);
  const sel = new Set(nextSelected);
  const kept = previousDeselected.filter((id) => !current.has(id));
  const now = allIds.filter((id) => !sel.has(id) && !uncertainIds.has(id));
  return [...new Set([...kept, ...now])];
}

const parseLegacy = (id: string): { idx: number; name: string } | null => {
  const m = /^rec-(\d+)-(.*)$/s.exec(id);
  return m ? { idx: Number(m[1]), name: normalisePart(m[2]) } : null;
};

/**
 * Old work orders stored only the TICKED, position-based ids (`rec-<n>-<name>`).
 * Ticked names are matched by name (all technologies of a name share its state).
 * The gaps in the old positions tell how many old parts were unticked between
 * two ticked ones; unmatched current parts in such a span are treated as
 * unticked when they fit in the gap, otherwise (more parts than the gap, so
 * new rows were added) — and after the last ticked part, where the old list
 * length is unknown — they start ticked. Non-engine ids compare exactly.
 * Returns the deselected ids for the current parts.
 */
export function migrateLegacySelection(
  oldSelectedIds: string[],
  currentParts: { id: string; name: string }[],
  uncertainIds: ReadonlySet<string> = new Set(),
): string[] {
  const oldSelected = new Set(oldSelectedIds);
  const anchors = oldSelectedIds
    .map(parseLegacy)
    .filter((x): x is { idx: number; name: string } => !!x)
    .sort((a, b) => a.idx - b.idx);
  const tickedNames = new Set(anchors.map((a) => a.name));
  const deselected = new Set<string>();

  // Exact ids (custom / legacy DB components) keep their own ids.
  for (const p of currentParts) {
    if (!isEnginePartId(p.id) && oldSelectedIds.length > 0 && !oldSelected.has(p.id)) {
      deselected.add(p.id);
    }
  }
  if (anchors.length === 0) return [...deselected];

  // Engine parts in current order, grouped by name (one old row per name).
  const engine = currentParts.filter((p) => isEnginePartId(p.id));
  const names: string[] = [];
  for (const p of engine) {
    const n = normalisePart(p.name);
    if (!names.includes(n)) names.push(n);
  }
  const idsByName = new Map<string, string[]>();
  for (const p of engine) {
    const n = normalisePart(p.name);
    idsByName.set(n, [...(idsByName.get(n) ?? []), p.id]);
  }

  let prevOldIdx = -1;
  let prevPos = -1;
  for (const a of anchors) {
    const pos = names.indexOf(a.name, prevPos + 1);
    if (pos < 0) continue; // ticked part no longer exists
    const gap = a.idx - prevOldIdx - 1;
    const span = names.slice(prevPos + 1, pos).filter((n) => !tickedNames.has(n));
    if (span.length > 0 && span.length <= gap) {
      for (const n of span) for (const id of idsByName.get(n) ?? []) deselected.add(id);
    }
    prevOldIdx = a.idx;
    prevPos = pos;
  }
  for (const id of [...deselected]) if (uncertainIds.has(id)) deselected.delete(id);
  return [...deselected];
}

export interface MergedUncertainRow<T> {
  key: string;
  name: string;
  technologies: PartTechnology[];
  items: T[];
}

/** Display-only: one row per name + category in the "Technology unclear" group. */
export function mergeUncertainByName<T extends { id: string; name: string; material?: string; technology?: PartTechnology | null }>(
  uncertain: T[],
): MergedUncertainRow<T>[] {
  const map = new Map<string, MergedUncertainRow<T>>();
  for (const u of uncertain) {
    const key = `${normalisePart(u.name)}|${normalisePart(u.material)}`;
    let row = map.get(key);
    if (!row) {
      row = { key, name: u.name, technologies: [], items: [] };
      map.set(key, row);
    }
    row.items.push(u);
    const tech = u.technology;
    if (tech && tech !== "all" && !row.technologies.includes(tech)) row.technologies.push(tech);
  }
  return [...map.values()];
}

const STEP_ONE_FIELDS = ["selectedComponentIds", "deselectedComponentIds", "customComponents"] as const;

/**
 * FIX-273: persist patch for a harvest sub-step. The step-1 fields are kept
 * only for harvestStep 1 or the frozen "Continue to extraction" save; for
 * later steps they are omitted so the runner's merge keeps the stored values.
 */
export function stepPersistPatch<T extends { harvestStep: number; freezeStepOne?: boolean }>(
  patch: T,
): Omit<T, "freezeStepOne"> {
  const { freezeStepOne, ...rest } = patch;
  if (patch.harvestStep <= 1 || freezeStepOne) return rest;
  const out: Record<string, unknown> = { ...rest };
  for (const k of STEP_ONE_FIELDS) delete out[k];
  return out as Omit<T, "freezeStepOne">;
}

/**
 * FIX-312: one entry per part name (trim + lowercase) for pick lists. When a
 * name has several entries, the one matching the appliance's known drying
 * technology wins, otherwise the first. Result is sorted by name.
 */
export function uniqueByName<T extends { id: string; name: string; technology?: string | null }>(
  items: T[],
  knownTechnology: string | null | undefined,
): T[] {
  const known = normalisePart(knownTechnology);
  const map = new Map<string, T>();
  for (const item of items) {
    const key = normalisePart(item.name);
    const kept = map.get(key);
    if (!kept) {
      map.set(key, item);
      continue;
    }
    const keptMatches = !!known && normalisePart(kept.technology) === known;
    const itemMatches = !!known && normalisePart(item.technology) === known;
    if (itemMatches && !keptMatches) map.set(key, item);
  }
  return [...map.values()].sort((a, b) => normalisePart(a.name).localeCompare(normalisePart(b.name)));
}
